import axios from 'axios';
import Vue from 'vue';
import VueRouter from 'vue-router';
import Vuex from 'vuex';
import VueI18n from 'vue-i18n';
import Vuetify from 'vuetify';
import VueApexCharts from 'vue-apexcharts';
import { Workbox } from 'workbox-window';
import * as Sentry from '@sentry/vue';
import 'material-design-icons-iconfont/dist/material-design-icons.css';
import '@mdi/font/css/materialdesignicons.css';
import 'vuetify/dist/vuetify.min.css';
import '../css/app.css';
/* eslint-disable-next-line import/no-webpack-loader-syntax,import/no-unresolved */
import languageBundle from '@kirschbaum-development/laravel-translations-loader?parameters={$1}!@kirschbaum-development/laravel-translations-loader';
import * as FullStory from '@fullstory/browser';
import SentryFullStory from '@sentry/fullstory';
import createRouter from './router';
import createStore from './store';

import MainLayout from './components/layouts/main-layout.vue';
import CenteredCard from './components/layouts/centered-card.vue';

import UserDetails from './components/screens/users/user-details.vue';
import ChangeEmail from './components/screens/auth/change-email.vue';
import ChangePassword from './components/screens/auth/change-password.vue';
import UserActivity from './components/screens/users/user-activity.vue';
import AccountDetails from './components/screens/account/account-details.vue';

Vue.use(VueApexCharts);
Vue.use(VueRouter);
Vue.use(Vuex);
Vue.use(VueI18n);
Vue.use(Vuetify);

Vue.component('Apexchart', VueApexCharts);

Vue.component(MainLayout.name, MainLayout);
Vue.component(CenteredCard.name, CenteredCard);

Vue.component(UserDetails.name, UserDetails);
Vue.component(ChangeEmail.name, ChangeEmail);
Vue.component(ChangePassword.name, ChangePassword);
Vue.component(UserActivity.name, UserActivity);

Vue.component(AccountDetails.name, AccountDetails);

// axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';
Vue.prototype.http = axios;
Vue.http = axios;

// Security: Prevent loading the app in a frame or cross-origin environment
if (window.top !== window || window.frameElement) {
  throw new Error('Frame detected, app load aborted for security purposes.');
}

// Security: Warn users against Self-XSS in the console
if (window.console) {
  // eslint-disable-next-line no-console
  setTimeout(console.log.bind(console, '%c%s', 'color: red; background: yellow; font-size: 24px;', 'WARNING!'));
  // eslint-disable-next-line no-console
  setTimeout(console.log.bind(console, '%c%s', 'font-size: 18px;', "Using this console may allow attackers to impersonate you and steal your information using an attack called Self-XSS.\nDo not enter or paste code that you don't understand."));
}

export const LICENSE_LITE = 'lite';
export const LICENSE_PLUS = 'plus';
export const LICENSE_PREMIUM = 'premium';
export const LICENSE_ENTERPRISE = 'enterprise';

// Get initial URL parameters for later use - note this will not change on SPA navigation
const params = new Proxy(new URLSearchParams(window.location.search), {
  get: (searchParams, prop) => searchParams.get(prop),
});

// Determine initial user, url or installation language
let locale = 'en';
if (window && window.user && window.user.language) {
  // User logged in with language prefs
  locale = window.user.language;
} else if (window.app && window.app.config && window.app.config.languages) {
  // Guest user - use URL lang param (if set and valid) or default installation lang
  const supportedLangs = Object.keys(window.app.config.languages);
  if (params.lang && params.lang.length >= 2 && params.lang.length <= 5 && supportedLangs.includes(params.lang)) {
    locale = params.lang;
    // eslint-disable-next-line no-console
    console.log('[i18n] A valid lang parameter was found, this will override the app default until a user is logged in.', locale);
  } else {
    locale = window.app.locale;
  }
} else {
  // No lang data, fallback to en, log an error.
  // eslint-disable-next-line no-console
  console.error('[i18n] Failed to determine user or app language preference on load, EN fallback will be used!');
  locale = 'en';
}

const i18n = new VueI18n({
  locale,
  messages: languageBundle,
});
const store = createStore(axios, i18n);
const router = createRouter(store);

if (process.env.MIX_DILIGENCE_FULLSTORY_ENABLED === 'true') {
  FullStory.init({
    orgId: process.env.MIX_DILIGENCE_FULLSTORY_ID ? process.env.MIX_DILIGENCE_FULLSTORY_ID : 'o-1AX17E-na1',
  });
  Vue.prototype.$FullStory = FullStory;
} else {
  /* eslint-disable-next-line no-console */
  console.log('FullStory is disabled in this environment');
}

if (process.env.MIX_DILIGENCE_SENTRY_ENABLED === 'true') {
  const sentryIntegrations = [];

  // Sentry Browser Tracing and Vue Router Instrumentation
  if (process.env.MIX_DILIGENCE_SENTRY_BROWSER_ENABLED === 'true') {
    sentryIntegrations.push(
      Sentry.browserTracingIntegration({
        router,
        tracePropagationTargets: ['diligence.cloud', 'dev.diligence.cloud', /^\//],
        tracesSampleRate: 0.1, // Capture 10% of all traces
      }),
    );
  }

  // Sentry Session Replay
  if (process.env.MIX_DILIGENCE_SENTRY_REPLAY_ENABLED === 'true') {
    sentryIntegrations.push(
      Sentry.replayIntegration({}),
    );
  }

  // Sentry FullStory Integration (If enabled)
  if (process.env.MIX_DILIGENCE_FULLSTORY_ENABLED === 'true' && typeof FullStory !== 'undefined') {
    sentryIntegrations.push(
      new SentryFullStory(`${process.env.MIX_DILIGENCE_SENTRY_ORG}`, { client: FullStory }),
    );
  }

  Sentry.init({
    Vue,
    dsn: process.env.MIX_DILIGENCE_SENTRY_DSN ? process.env.MIX_DILIGENCE_SENTRY_DSN : 'https://08a578eab2ae46ffae4b579b02d997e6@o132413.ingest.sentry.io/6154007',
    replaysSessionSampleRate: 0.1, // Capture 10% of all sessions
    replaysOnErrorSampleRate: 1.0, // Of the remaining 90% of sessions, if an error happens start capturing
    integrations: sentryIntegrations,
    // Set tracesSampleRate to 1.0 to capture 100% of transactions for performance monitoring.
    // We recommend adjusting this value in production
    tracesSampleRate: 0.1,
    attachProps: true,
    logErrors: true,
    trackComponents: true,
    hooks: ['activate', 'create', 'destroy', 'mount', 'unmount', 'update'],
    release: `${process.env.MIX_DILIGENCE_SENTRY_PROJECT}@${process.env.MIX_DILIGENCE_FRONTEND_VERSION}`,
  });
  Vue.prototype.$Sentry = Sentry;
} else {
  /* eslint-disable-next-line no-console */
  console.log('Sentry is disabled in this environment');
}
// Sentry.setTag('app_scope', 'frontend');

if (typeof process.env.MIX_DILIGENCE_FRONTEND_VERSION === 'undefined') {
  /* eslint-disable-next-line no-console */
  console.error('Frontend version missing from build! Ensure environment is setup correctly.');
}

const app = new Vue({
  router,
  store,
  i18n,
  vuetify: new Vuetify({
    theme: {
      disable: true,
      // themes: {
      //   light: {
      //     primary: '#33446b',
      //     secondary: '#ce1842',
      //     accent: '#ffc72c',
      //     error: '#ce1842',
      //   },
      // },
    },
  }),
  render(h) {
    return h(this.$route.meta.providesOwnLayout ? 'router-view' : 'main-layout');
  },
});

router.onReady(() => app.$mount('#diligence-app'));

router.onError(error => {
  if (/loading chunk \d* failed./i.test(error.message)) {
    /* eslint-disable-next-line no-console */
    console.warn('Expired chunks detected, restarting app...');
    /* eslint-disable-next-line no-alert */
    alert('A newer version of this platform is available, the page will now reload.');
    window.location.reload();
  }
});

// @TODO Remove the special key and counter when Service Worker is ready for prod use
let wbCounter = 0;
document.addEventListener('keyup', event => {
  if (event.key !== '§') return;
  wbCounter += 1;
  if (wbCounter < 3) return;
  /* eslint-disable-next-line no-console */
  console.log('Service worker alpha enabled, SW will now be registered on this device!');
  if ('serviceWorker' in navigator) {
    const wb = new Workbox('/service-worker.js');
    /* eslint-disable-next-line no-console */
    wb.register().then(r => console.debug('Workbox registered', r));
  }
});
